import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  createLeave,
  getLeaveById,
  postLeaveDecision,
  postLeaveReplacement,
  getLeavesWorkspace,
  updateLeave,
} from '@/api/services/leaves.service'
import { getErrorMessage } from '@/lib/getErrorMessage'
import { attendanceKeys } from '@/features/attendance/hooks/useAttendance'
import { shiftAssignmentsQueryKeys } from '@/features/shifts/hooks/useShifts'
import {
  EMPLOYEE_NOT_ELIGIBLE,
  describeEligibilityReasons,
} from '@/features/shifts/utils/shiftEligibility'
import { invalidateDashboards } from '@/features/dashboard/hooks/useDashboardStats'
import type {
  CreateLeaveRequestDto,
  UpdateLeaveRequestDto,
  LeaveDecisionDto,
  AssignReplacementDto,
  MyLeaveListQuery,
} from '@/features/leave/types/leave.types'

export const leaveKeys = {
  all: (companyId: string | null) => ['leaves', companyId] as const,
  detail: (companyId: string | null, leaveId: string) =>
    ['leaves', companyId, 'detail', leaveId] as const,
  workspaceRoot: ['leaves', 'workspace'] as const,
  workspace: (params: MyLeaveListQuery | undefined) => ['leaves', 'workspace', params] as const,
}

export function useLeavesWorkspace(
  params: MyLeaveListQuery | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: leaveKeys.workspace(params),
    queryFn: () => getLeavesWorkspace(params),
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: options?.enabled !== false,
  })
}

export function useLeaveDetail(companyId: string | null, leaveId: string | null) {
  return useQuery({
    queryKey: leaveKeys.detail(companyId, leaveId ?? ''),
    queryFn: () => getLeaveById(companyId!, leaveId!),
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!companyId && !!leaveId,
  })
}

export function useCreateLeave() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, body }: { companyId: string; body: CreateLeaveRequestDto }) =>
      createLeave(companyId, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: leaveKeys.all(variables.companyId) })
      queryClient.invalidateQueries({ queryKey: leaveKeys.workspaceRoot })
      // MyLeavesPage reads from the ['leaves-me', ...] key; refresh it too so a
      // newly submitted request appears immediately in the self-service list.
      queryClient.invalidateQueries({ queryKey: ['leaves-me'] })
      void invalidateDashboards(queryClient)
      toast.success('Leave request submitted.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useUpdateLeave() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      companyId,
      leaveId,
      body,
    }: {
      companyId: string
      leaveId: string
      body: UpdateLeaveRequestDto
    }) => updateLeave(companyId, leaveId, body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: leaveKeys.all(variables.companyId) })
      queryClient.invalidateQueries({
        queryKey: leaveKeys.detail(variables.companyId, variables.leaveId),
      })
      queryClient.invalidateQueries({ queryKey: ['leaves-me'] })
      queryClient.invalidateQueries({ queryKey: leaveKeys.workspaceRoot })
      void invalidateDashboards(queryClient)
      toast.success('Leave request updated.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useLeaveDecision() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      companyId,
      leaveId,
      body,
    }: {
      companyId: string
      leaveId: string
      body: LeaveDecisionDto
    }) => postLeaveDecision(companyId, leaveId, body),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: leaveKeys.all(variables.companyId) })
      queryClient.invalidateQueries({ queryKey: leaveKeys.workspaceRoot })
      queryClient.invalidateQueries({ queryKey: ['leaves-me'] })
      queryClient.invalidateQueries({
        queryKey: attendanceKeys.all(variables.companyId),
      })
      queryClient.invalidateQueries({
        queryKey: shiftAssignmentsQueryKeys.all(variables.companyId),
      })
      void invalidateDashboards(queryClient)
      const action = result.status === 'rejected' ? 'rejected' : 'approved'
      toast.success(`Leave request ${action}.`)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
      // The request may have been decided elsewhere (e.g. already approved/rejected) —
      // refetch so the stale pending row disappears from lists and the dashboard.
      queryClient.invalidateQueries({ queryKey: leaveKeys.workspaceRoot })
      void invalidateDashboards(queryClient)
    },
  })
}

export function useLeaveReplacement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      companyId,
      leaveId,
      body,
    }: {
      companyId: string
      leaveId: string
      body: AssignReplacementDto
    }) => postLeaveReplacement(companyId, leaveId, body),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: leaveKeys.all(variables.companyId) })
      queryClient.invalidateQueries({ queryKey: leaveKeys.workspaceRoot })
      queryClient.invalidateQueries({ queryKey: ['leaves-me'] })
      queryClient.invalidateQueries({
        queryKey: attendanceKeys.all(variables.companyId),
      })
      queryClient.invalidateQueries({
        queryKey: shiftAssignmentsQueryKeys.all(variables.companyId),
      })
      void invalidateDashboards(queryClient)
      const handoff = result.shiftHandoff
      const hasWarnings =
        (handoff?.conflicts?.length ?? 0) > 0 || (handoff?.skipped?.length ?? 0) > 0
      if (hasWarnings) {
        const conflictCount = handoff?.conflicts?.length ?? 0
        const skippedCount = handoff?.skipped?.length ?? 0
        const parts: string[] = []
        if (conflictCount > 0)
          parts.push(`${conflictCount} conflict${conflictCount > 1 ? 's' : ''}`)
        if (skippedCount > 0) parts.push(`${skippedCount} skipped`)
        const ineligible = (handoff?.skipped ?? []).filter(
          (item) => item.code === EMPLOYEE_NOT_ELIGIBLE
        )
        const ineligibleNote =
          ineligible.length > 0
            ? ` ${ineligible.length} day${ineligible.length > 1 ? 's' : ''} not handed over because ${
                describeEligibilityReasons([
                  ...new Map(
                    ineligible.flatMap((item) => item.reasons ?? []).map((r) => [r.code, r])
                  ).values(),
                ]) || 'the replacement cannot work them'
              }.`
            : ''
        toast.warning(
          `Replacement assigned with shift handoff issues: ${parts.join(', ')}.${ineligibleNote}`
        )
      } else {
        toast.success('Replacement assigned.')
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}
